<template>
	<section :class="['table-container', stickyTableContainerStyle]" :dir="tableDir">
		<!-- Mobile Sorting Dropdown - Only visible on mobile -->
		<div class="mobile-sort-dropdown" v-if="showMobileSorting">
			<label class="sort-label">{{ $t('sort_by') }}:</label>
			<div class="custom-select-wrapper">
				<button class="custom-select-button" @click="toggleDropdown" :class="{ active: isDropdownOpen }">
					<span class="selected-text">
						{{ getSelectedFieldLabel() }}
					</span>
					<svg :class="{ rotated: isDropdownOpen }" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
						<path d="M7 10l5 5 5-5z" />
					</svg>
				</button>
				<div class="custom-dropdown" v-show="isDropdownOpen" ref="dropdown">
					<div
						v-for="field in analyticsTableInfo"
						:key="field"
						class="dropdown-option"
						:class="{ selected: field === sortBy }"
						@click="selectOption(field)"
					>
						{{ getSelectedFieldLabel(field) }}
					</div>
				</div>
			</div>
			<button
				class="sort-direction-btn"
				@click="toggleSortDirection"
				:title="isAsc ? $t('ascending') : $t('descending')"
			>
				<svg :class="{ rotated: !isAsc }" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
					<path d="M7 14l5-5 5 5z" />
				</svg>
			</button>
		</div>

		<div class="table-head table-row" :class="[isDrilldownMode ? 'narrowed' : '', stickyTableStyle]">
			<div
				class="table-cell header-cell"
				:class="{ 
					'active-sort': field === sortBy,
					'long-header': field === 'maintenanceComplaints'
				}"
				v-for="field in analyticsTableInfo"
				:key="field"
				@click="changeSort(field)"
			>
				<span class="header-text">
					{{ getSelectedFieldLabel(field) }}
				</span>
				<div class="sort-indicator" v-if="field === sortBy">
					<svg :class="{ rotated: !isAsc }" width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
						<path d="M7 14l5-5 5 5z" />
					</svg>
				</div>
			</div>
		</div>

		<div class="table-body-container">
			<div
				class="table-row data-row"
				:class="[
					isDrilldownMode ? 'narrowed' : ''
				]"
				v-for="(room, index) in rooms"
				:key="room._id || index"
				@click="handleTableClick(room._id)"
			>
				<div
					v-for="field in analyticsTableInfo"
					:key="field"
					class="table-cell data-cell"
					:class="{
						'station-name': field === 'displayName',
						'score-container': field === 'score',
						'metric-cell': ['exposures'].includes(field)
					}"
					:dir="field === 'displayName' ? 'auto' : undefined"
					:data-label="getFieldLabel(field)"
				>
					<template v-if="field === 'score'">
						<div class="score-wrapper">
							<div class="score-display">
								<span class="score-value">{{ (room.score && room.score?.toFixed(0)) || 'N/A' }}</span>
								<div
									class="score-ring"
									:style="{
										background: `conic-gradient(${getScoreColor(room.score)} ${room.score || 0}%, #e5e7eb 0%)`
									}"
								>
									<div class="score-inner"></div>
								</div>
							</div>
						</div>
					</template>

					<template v-else-if="field === 'avgResponseTime'">
						<div class="metric-with-icon">
							<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" class="metric-icon">
								<path
									d="M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M12,4A8,8 0 0,1 20,12A8,8 0 0,1 12,20A8,8 0 0,1 4,12A8,8 0 0,1 12,4M12.5,7V12.25L17,14.92L16.25,16.15L11,13V7H12.5Z"
								/>
							</svg>
							<span>{{ room.avgResponseTime | timePassed }}</span>
						</div>
					</template>

					<template v-else-if="field === 'exposures'">
						<div class="percentage-cell">
							<span class="percentage-value" :class="getPercentageClass(room[field])">
								{{ room[field]?.toFixed() }}%
							</span>
							<div class="percentage-bar">
								<div class="percentage-fill" :style="{ width: `${Math.min(room[field] || 0, 100)}%` }"></div>
							</div>
						</div>
					</template>

					<template v-else-if="field === 'visits'">
						<div class="visits-container">
							<span class="visits-count">{{ room[field] || 0 }}</span>
							<div class="visits-badges">
								<span v-if="room.isAmended && isAmendedPermitted" class="amendment-badge">
									<svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
										<path
											d="M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2M11,16.5L18,9.5L16.5,8L11,13.5L7.5,10L6,11.5L11,16.5Z"
										/>
									</svg>
								</span>
							</div>
						</div>
					</template>

					<template v-else-if="field === 'arrivals'">
						<div class="visits-container">
							<span class="visits-count">{{ room[field] || 0 }}</span>
							<div class="visits-badges">
								<span v-if="room.isArrivalsAmended && isAmendedPermitted" class="amendment-badge">
									<svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
										<path
											d="M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2M11,16.5L18,9.5L16.5,8L11,13.5L7.5,10L6,11.5L11,16.5Z"
										/>
									</svg>
								</span>
							</div>
						</div>
					</template>

					<template v-else-if="field === 'unnecessaryArrivals'">
						<div class="default-cell">
							<span class="default-value">{{ getUnnecessaryArrivalsText(room) }}</span>
						</div>
					</template>

						<template v-else-if="field === 'ratio'">
						<div class="ratio-cell">
							<span class="ratio-value" :class="getRatioClass(room)">
								{{ getRoomRatio(room) }}
							</span>
						</div>
					</template>

					<template v-else-if="field === 'topComplaints'">
						<div class="top-complaints-cell">
							<div v-if="room.topComplaints && room.topComplaints.name" class="complaint-info">
								<span class="complaint-name" :title="room.topComplaints.name">
									{{ truncateComplaintName(room.topComplaints.name) }}
								</span>
								<div class="complaint-percentage">
									<span
										class="percentage-value"
										:class="getComplaintPercentageClass(room.topComplaints.percentage)"
									>
										{{ room.topComplaints.percentage }}%
									</span>
									<div class="percentage-bar">
										<div
											class="percentage-fill"
											:style="{ width: `${room.topComplaints.percentage}%` }"
										></div>
									</div>
								</div>
							</div>
							<div v-else class="no-complaints">
								<span class="no-data-text">{{ $t('no_complaints') }}</span>
							</div>
						</div>
					</template>

					<template v-else>
						<div class="default-cell">
							<span class="default-value">{{ room[field] }}</span>
						</div>
					</template>
				</div>
			</div>
		</div>

		<div v-if="!rooms.length" class="empty-state">
			<div class="empty-icon-wrapper">
				<svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor" class="empty-icon">
					<path
						d="M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12A10,10 0 0,1 12,2M12,4A8,8 0 0,1 20,12A8,8 0 0,1 12,20A8,8 0 0,1 4,12A8,8 0 0,1 12,4M11,16.5L18,9.5L16.5,8L11,13.5L7.5,10L6,11.5L11,16.5Z"
					/>
				</svg>
			</div>
			<h3>{{ $t('no_data_available') }}</h3>
			<p>{{ $t('no_rooms_found') }}</p>
		</div>
	</section>
</template>

<script>
import { analyticsTableInfo, analyticsTableInfoNarrowed } from '../consts/consts'
import { hasServikNameFilter } from '../../common/services/filterService'
import { getUnnecessaryArrivalsShareText } from '../services/performanceService'
import { mapGetters } from 'vuex'

export default {
	name: 'roomTable',
	props: {
		rooms: { type: Array, required: true },
		hideCols: { type: Array, required: false, default: () => [] },
		isExporting: { type: Boolean, required: false, default: false }
	},
	data() {
		return {
			sortBy: 'score',
			isAsc: false,
			showMobileSorting: false,
			isDropdownOpen: false
		}
	},
	mounted() {
		this.$emit('mounted')
		this.checkMobileView()
		window.addEventListener('resize', this.checkMobileView)
		document.addEventListener('click', this.handleClickOutside)
		// Apply default sorting
		this.applySorting()
	},
	beforeDestroy() {
		window.removeEventListener('resize', this.checkMobileView)
		document.removeEventListener('click', this.handleClickOutside)
	},
	computed: {
		...mapGetters(['loggedUser', 'isRTL']),
		isDrilldownMode() {
			const filter = this.$store.getters.filterSelected
			if (filter.hour || filter.hour === 0 || hasServikNameFilter(filter.servikName)) return true
			return false
		},
		analyticsTableInfo() {
			const allCleandexCalls = this.rooms.every((room) => room.cleandexCalls === 0)
			const allManualCalls = this.rooms.every((room) => room.manualCalls === 0)
			const noUnnecessaryArrivals = this.rooms.every((room) => room.unnecessaryArrivals === null || room.unnecessaryArrivals === undefined)
			if (this.isDrilldownMode) return analyticsTableInfoNarrowed.head
			const tableColsToHide = []
			if (allCleandexCalls) tableColsToHide.push('cleandexCalls')
			if (allManualCalls) tableColsToHide.push('manualCalls')
			if (noUnnecessaryArrivals) tableColsToHide.push('unnecessaryArrivals')
			if (this.hideCols.length > 0)
				return analyticsTableInfo.head.filter(
					(col) => !tableColsToHide.includes(col) && !this.hideCols.includes(col)
				)
			return analyticsTableInfo.head.filter((col) => !tableColsToHide.includes(col))
		},
		isAmendedPermitted() {
			const { level } = this.loggedUser
			return level === 'buzzz'
		},
		stickyTableStyle() {
			if (this.isExporting) {
				return ''
			} else {
				return 'sticky-table-head'
			}
		},
		stickyTableContainerStyle() {
			if (this.isExporting) {
				return ''
			} else {
				return 'sticky-table-container'
			}
		},
		tableDir() {
			if (!this.isExporting) return undefined
			return this.isRTL ? 'rtl' : 'ltr'
		},
	},
	methods: {
		checkMobileView() {
			this.showMobileSorting = window.innerWidth <= 768
		},
		toggleDropdown() {
			this.isDropdownOpen = !this.isDropdownOpen
		},
		selectOption(field) {
			this.sortBy = field
			this.isAsc = true
			this.isDropdownOpen = false
			this.applySorting()
		},
		getSelectedFieldLabel(field = null) {
			const targetField = field || this.sortBy
			if (targetField === 'arrivals') return this.$t('cleaner_arrivals')
			if (targetField === 'unnecessaryArrivals') return this.$t('unnecessary_arrivals')
			if (targetField === 'topComplaints') return this.$t('top_complaint')
			if (targetField === 'maintenanceComplaints') return this.$t('maintenance_complaints')
			if (targetField === 'likes') return this.$t('likes')
			return this.$t(targetField)
		},
		handleClickOutside(event) {
			if (this.$refs.dropdown && !this.$el.contains(event.target)) {
				this.isDropdownOpen = false
			}
		},
		handleMobileSortChange(event) {
			const field = event.target.value
			if (field) {
				this.sortBy = field
				this.isAsc = true
				this.applySorting()
			}
		},
		toggleSortDirection() {
			if (this.sortBy) {
				this.isAsc = !this.isAsc
				this.applySorting()
			}
		},
		handleTableClick(id) {
			this.$emit('click', id)
		},
		getRoomRatio(room) {
			if (!room.arrivals) return 'N/A'
			return (room.visits / room.arrivals)?.toFixed(0) || 'N/A'
		},
		getUnnecessaryArrivalsText(room) {
			if (room.unnecessaryArrivals === null || room.unnecessaryArrivals === undefined) return 'N/A'
			if (!room.checkedArrivals) return `${room.unnecessaryArrivals}`
			const shareText = getUnnecessaryArrivalsShareText({ count: room.unnecessaryArrivals, checkedArrivals: room.checkedArrivals })
			return `${room.unnecessaryArrivals} ${shareText}`
		},
		changeSort(sortBy) {
			if (this.sortBy === sortBy) {
				this.isAsc = !this.isAsc
			} else {
				this.sortBy = sortBy
				this.isAsc = true
			}
			this.applySorting()
		},

		applySorting() {
			let sortBy = this.sortBy
			const isAsc = this.isAsc

			if (sortBy === 'room_name') sortBy = 'displayName'
			else if (sortBy === 'avg_response_time') sortBy = 'avgResponseTime'
			else if (sortBy === 'missed_sla') sortBy = 'missedSla'
			else if (sortBy === 'inspections') sortBy = 'supervisors'

			this.rooms.sort((a, b) => {
				if (sortBy === 'displayName') {
					if (isAsc) return a[sortBy] > b[sortBy] ? -1 : 1
					else return a[sortBy] > b[sortBy] ? 1 : -1
				} else if (sortBy === 'ratio') {
					if (isAsc) return this.getRoomRatio(a) - this.getRoomRatio(b)
					else return this.getRoomRatio(b) - this.getRoomRatio(a)
				} else if (sortBy === 'topComplaints') {
					// Sort by top complaint percentage
					const aPercentage = (a.topComplaints && a.topComplaints.percentage) || 0
					const bPercentage = (b.topComplaints && b.topComplaints.percentage) || 0
					if (isAsc) return bPercentage - aPercentage
					else return aPercentage - bPercentage
				} else {
					if (isAsc) return b[sortBy] - a[sortBy]
					else return a[sortBy] - b[sortBy]
				}
			})
		},

		getScoreColor(score) {
			if (!score) return '#e5e7eb'
			if (score >= 80) return '#10b981'
			if (score >= 60) return '#f59e0b'
			return '#ef4444'
		},

		getPercentageClass(value) {
			if (!value) return 'low'
			if (value >= 75) return 'high'
			if (value >= 50) return 'medium'
			return 'low'
		},

		getRatioClass(room) {
			const ratio = this.getRoomRatio(room)
			if (ratio === 'N/A') return 'unknown'
			const num = parseInt(ratio)
			if (num >= 80) return 'high'
			if (num >= 50) return 'medium'
			return 'low'
		},

		// New methods for top complaints
		truncateComplaintName(name) {
			if (!name) return ''
			return name.length > 20 ? name.substring(0, 20) + '...' : name
		},

		getComplaintPercentageClass(percentage) {
			if (!percentage) return 'none'
			if (percentage >= 50) return 'very-high'
			if (percentage >= 30) return 'medium'
			return 'low'
		},

		getFieldLabel(field) {
			const labels = {
				displayName: this.$t('room_name'),
				score: this.$t('score'),
				visits: this.$t('visits'),
				complaints: this.$t('complaints'),
				topComplaints: this.$t('top_complaint'),
				arrivals: this.$t('cleaner_arrivals'),
				unnecessaryArrivals: this.$t('unnecessary_arrivals'),
				exposures: this.$t('exposures'),
				avgResponseTime: this.$t('response_time_SLA'),
				ratio: this.$t('ratio'),
				cleandexCalls: this.$t('cleandex_calls'),
				manualCalls: this.$t('manual_calls'),
				likes: this.$t('likes'),
				maintenanceComplaints: this.$t('maintenance_complaints')
			}
			return labels[field] || this.$t(field) || field
		},

	}
}
</script>

<style lang="scss" scoped>
@use '@/styles/mixins' as *;

.table-container {
	width: 100%;
	margin-inline-start: 0px;
	border-radius: 16px;
	background: #ffffff;
	box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
	overflow: hidden;
	border: 1px solid #f1f5f9;

	&.sticky-table-container {
		overflow: auto;
		max-height: 80vh;
		scroll-behavior: smooth;
	}

	// Mobile sorting dropdown - only visible on mobile
	.mobile-sort-dropdown {
		display: none;
		@include align-center;
		gap: 12px;
		padding: 12px 16px;
		background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
		border-bottom: 1px solid #e2e8f0;
		position: relative;
		z-index: 100;

		.sort-label {
			font-weight: 600;
			font-size: 12px;
			color: #475569;
			text-transform: uppercase;
			letter-spacing: 0.5px;
			white-space: nowrap;
		}

		.custom-select-wrapper {
			position: relative;
			flex: 1;

			.custom-select-button {
				width: 100%;
				padding: 8px 12px;
				border: 1px solid #d1d5db;
				border-radius: 8px;
				background: white;
				font-size: 13px;
				color: #374151;
				font-weight: 500;
				display: flex;
				align-items: center;
				justify-content: space-between;
				cursor: pointer;
				transition: all 0.2s ease;

				&:focus,
				&.active {
					outline: none;
					border-color: #3b82f6;
					box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
				}

				.selected-text {
					flex: 1;
					text-align: start;
				}

				svg {
					transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
					margin-inline-start: 8px;

					&.rotated {
						transform: rotate(180deg);
					}
				}
			}

			.custom-dropdown {
				position: absolute;
				top: 100%;
				left: 0;
				right: 0;
				background: white;
				border: 1px solid #d1d5db;
				border-radius: 8px;
				box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
				z-index: 1000;
				max-height: 200px;
				overflow-y: auto;
				margin-top: 2px;

				.dropdown-option {
					padding: 10px 12px;
					font-size: 13px;
					color: #374151;
					cursor: pointer;
					transition: background-color 0.2s ease;

					&:hover {
						background-color: #f8fafc;
					}

					&.selected {
						background-color: #eff6ff;
						color: #3b82f6;
						font-weight: 600;
					}

					&:first-child {
						border-top-left-radius: 8px;
						border-top-right-radius: 8px;
					}

					&:last-child {
						border-bottom-left-radius: 8px;
						border-bottom-right-radius: 8px;
					}
				}
			}
		}

		.sort-direction-btn {
			@include flex-center;
			width: 36px;
			height: 36px;
			border: 1px solid #d1d5db;
			border-radius: 8px;
			background: white;
			color: #475569;
			cursor: pointer;
			transition: all 0.2s ease;

			&:hover {
				background: #f8fafc;
				border-color: #3b82f6;
				color: #3b82f6;
			}

			&:active {
				transform: scale(0.95);
			}

			svg {
				transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);

				&.rotated {
					transform: rotate(180deg);
				}
			}
		}
	}

	.table-row {
		display: grid;
		grid-template-columns: minmax(140px, 1.5fr) repeat(auto-fit, minmax(80px, 1fr));
		border-bottom: 1px solid #f1f5f9;
		min-height: 48px;
		transition: all 0.2s ease;

		&.narrowed {
			grid-template-columns: 1.5fr 1fr 1.2fr 1fr 1.2fr 1fr;
		}

		&.data-row {
			cursor: pointer;

			&:hover {
				background: linear-gradient(135deg, #f8faff 0%, #f1f5ff 100%);
				box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
			}

			&:active {
				transform: translateY(0);
			}
		}

		.table-cell {
			@include flex-center;
			padding: 8px 12px;
			font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
			transition: all 0.2s ease;

			&.header-cell {
				font-weight: 600;
				font-size: 10px;
				letter-spacing: 0.5px;
				text-transform: uppercase;
				color: #475569;
				background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
				border-bottom: 2px solid #e2e8f0;
				cursor: pointer;
				padding: 10px 12px;
				word-break: normal;
				overflow-wrap: normal;

				&.active-sort {
					color: #3b82f6;
					background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
					border-bottom-color: #3b82f6;

					.sort-indicator {
						opacity: 1;
					}
				}

				&:hover {
					background: linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%);
					color: #1e293b;
				}

				&.long-header {
					line-height: 1.3;
					min-height: 50px;
					display: flex;
					flex-direction: column;
					justify-content: center;
					align-items: center;
					text-align: center;
					padding: 8px 6px;
					
					.header-text {
						line-height: 1.3;
						text-align: center;
						white-space: normal;
						word-break: normal;
						max-width: 100%;
					}
					
					.sort-indicator {
						margin-top: 4px;
						margin-inline-start: 0;
					}
				}

				.header-text {
					@include align-center;
					gap: 8px;
					word-break: normal;
					line-height: 1.4;
				}

				.sort-indicator {
					opacity: 0.7;
					transition: all 0.3s ease;
					margin-inline-start: 6px;

					svg {
						transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
						color: currentColor;

						&.rotated {
							transform: rotate(180deg);
						}
					}
				}
			}

			&.data-cell {
				font-size: 13px;
				color: #334155;
				font-weight: 500;
			}

			&.station-name {
				justify-content: flex-start;
				font-weight: 700;
				font-size: 14px;
				color: #1e293b;
				padding-inline-start: 16px;
				text-align: start;
				unicode-bidi: plaintext;
			}

			&.score-container {
				.score-wrapper {
					@include align-center;
					gap: 8px;

					.score-display {
						position: relative;
						width: 40px;
						height: 40px;

						.score-value {
							position: absolute;
							top: 50%;
							left: 50%;
							transform: translate(-50%, -50%);
							font-weight: 700;
							font-size: 13px;
							color: #1e293b;
							z-index: 2;
						}

						.score-ring {
							width: 100%;
							height: 100%;
							border-radius: 50%;
							position: relative;

							.score-inner {
								position: absolute;
								top: 4px;
								left: 4px;
								right: 4px;
								bottom: 4px;
								background: white;
								border-radius: 50%;
							}
						}
					}
				}
			}

			&.metric-cell {
				font-variant-numeric: tabular-nums;
			}
		}

		&.table-head {
			.table-cell:first-child {
				justify-content: flex-start;
				text-align: start;
			}
		}
	}

	&[dir='rtl'] {
		.table-row {
			direction: rtl;
		}

		.table-cell.header-cell {
			text-align: start;
		}
	}

	.sticky-table-head {
		position: sticky;
		top: 0;
		z-index: 99;
		background: #ffffff;
		backdrop-filter: blur(10px);
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
	}

	.metric-with-icon {
		@include align-center;
		gap: 6px;
		font-weight: 500;

		.status-dot {
			width: 6px;
			height: 6px;
			border-radius: 50%;
			flex-shrink: 0;

			&.good {
				background: #10b981;
			}
			&.warning {
				background: #f59e0b;
			}
			&.critical {
				background: #ef4444;
			}
			&.unknown {
				background: #6b7280;
			}
		}

		.metric-icon {
			color: #64748b;
			opacity: 0.8;
			width: 14px;
			height: 14px;
		}

		span {
			font-size: 12px;
			white-space: nowrap;
			overflow: hidden;
			text-overflow: ellipsis;
		}
	}

	.percentage-cell {
		display: flex;
		flex-direction: column;
		gap: 4px;
		width: 100%;
		align-items: center;

		.percentage-value {
			padding: 4px 8px;
			border-radius: 6px;
			font-weight: 600;
			font-size: 12px;

			&.high {
				background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
				color: #dc2626;
			}
			&.medium {
				background: linear-gradient(135deg, #fef3c7 0%, #fed7aa 100%);
				color: #d97706;
			}
			&.low {
				background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
				color: #0369a1;
			}
		}

		.percentage-bar {
			width: 100%;
			height: 3px;
			background: #e5e7eb;
			border-radius: 2px;
			overflow: hidden;

			.percentage-fill {
				height: 100%;
				background: linear-gradient(90deg, #3b82f6, #1d4ed8);
				border-radius: 2px;
				transition: width 0.6s ease;
			}
		}
	}

	.visits-container {
		@include align-center;
		gap: 6px;

		.visits-count {
			font-weight: 600;
			font-size: 14px;
			color: #1e293b;
		}

		.visits-badges {
			display: flex;
			gap: 3px;

			.amendment-badge,
			.critical-badge {
				@include flex-center;
				width: 14px;
				height: 14px;
				border-radius: 50%;
				color: white;
				font-size: 8px;
			}

			.amendment-badge {
				background: linear-gradient(135deg, #3b82f6, #1d4ed8);
			}

			.critical-badge {
				background: linear-gradient(135deg, #ef4444, #dc2626);
				font-weight: bold;
			}
		}
	}

	.ratio-cell {
		.ratio-value {
			padding: 4px 10px;
			border-radius: 8px;
			font-weight: 600;
			font-size: 13px;

			&.high {
				background: linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%);
				color: #166534;
			}
			&.medium {
				background: linear-gradient(135deg, #fef3c7 0%, #fed7aa 100%);
				color: #92400e;
			}
			&.low {
				background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
				color: #dc2626;
			}
			&.unknown {
				background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
				color: #475569;
			}
		}
	}

	// Top Complaints Cell Styles
	.top-complaints-cell {
		display: flex;
		flex-direction: column;
		gap: 4px;
		width: 100%;
		align-items: center;
		min-width: 120px;

		.complaint-info {
			display: flex;
			flex-direction: column;
			gap: 4px;
			width: 100%;
			align-items: center;

			.complaint-name {
				font-size: 11px;
				font-weight: 600;
				color: #374151;
				text-align: center;
				line-height: 1.2;
				max-width: 100%;
				word-wrap: break-word;
				cursor: help;
			}

			.complaint-percentage {
				display: flex;
				flex-direction: column;
				gap: 2px;
				width: 100%;
				align-items: center;

				.percentage-value {
					padding: 2px 6px;
					border-radius: 4px;
					font-weight: 700;
					font-size: 11px;
					min-width: 35px;
					text-align: center;

					&.very-high {
						background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
						color: #dc2626;
						border: 1px solid #fecaca;
					}
					&.high {
						background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
						color: #d97706;
						border: 1px solid #fed7aa;
					}
					&.medium {
						background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
						color: #2563eb;
						border: 1px solid #bfdbfe;
					}
					&.low {
						background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
						color: #059669;
						border: 1px solid #bbf7d0;
					}
					&.none {
						background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
						color: #64748b;
						border: 1px solid #e2e8f0;
					}
				}

				.percentage-bar {
					width: 80%;
					height: 3px;
					background: #e5e7eb;
					border-radius: 2px;
					overflow: hidden;

					.percentage-fill {
						height: 100%;
						background: linear-gradient(90deg, #3b82f6, #1d4ed8);
						border-radius: 2px;
						transition: width 0.6s ease;
					}
				}
			}
		}

		.no-complaints {
			@include flex-center;
			width: 100%;
			padding: 8px;

			.no-data-text {
				font-size: 11px;
				color: #9ca3af;
				font-style: italic;
			}
		}
	}

	.default-cell {
		.default-value {
			font-weight: 500;
			color: #475569;
			font-size: 13px;
		}
	}

	.empty-state {
		@include flex-center;
		flex-direction: column;
		padding: 64px 24px;
		color: #64748b;

		.empty-icon-wrapper {
			margin-bottom: 24px;

			.empty-icon {
				color: #cbd5e1;
			}
		}

		h3 {
			font-size: 20px;
			font-weight: 700;
			margin: 0 0 8px 0;
			color: #1e293b;
		}

		p {
			font-size: 16px;
			font-weight: 500;
			margin: 0;
			color: #64748b;
		}
	}

	// Responsive design
	@media (max-width: 1200px) {
		.table-row {
			grid-template-columns: minmax(120px, 1.2fr) repeat(auto-fit, minmax(70px, 1fr));

			&.narrowed {
				grid-template-columns: 1.2fr 1fr 1fr 1fr 1fr 1fr;
			}

			.table-cell {
				padding: 6px 8px;
				font-size: 12px;

				&.header-cell {
					font-size: 9px;
					padding: 8px;
				}

				&.station-name {
					font-size: 13px;
					padding-inline-start: 12px;
				}
			}
		}

		.top-complaints-cell {
			min-width: 100px;

			.complaint-info {
				.complaint-name {
					font-size: 10px;
				}

				.complaint-percentage {
					.percentage-value {
						font-size: 10px;
						padding: 2px 4px;
						min-width: 30px;
					}
				}
			}
		}
	}

	@media (max-width: 768px) {
		border-radius: 12px;
		margin: 0;

		// Show mobile sorting dropdown
		.mobile-sort-dropdown {
			display: flex;
		}

		.table-row {
			display: block;
			border-bottom: 1px solid #f1f5f9;
			padding: 12px;

			&.table-head {
				display: none;
			}

			.table-cell {
				@include align-center;
				justify-content: space-between;
				padding: 6px 0;
				border-bottom: none;

				&:before {
					content: attr(data-label);
					font-weight: 600;
					color: #64748b;
					font-size: 11px;
					text-transform: uppercase;
					letter-spacing: 0.5px;
					min-width: 100px;
				}

				&.station-name {
					font-weight: 700;
					font-size: 16px;
					justify-content: flex-start;
					padding: 8px 0;
					border-bottom: 1px solid #f1f5f9;
					margin-bottom: 8px;

					&:before {
						display: none;
					}
				}

				&.score-container {
					.score-wrapper {
						.score-display {
							width: 32px;
							height: 32px;

							.score-value {
								font-size: 11px;
							}
						}
					}
				}
			}
		}

		.top-complaints-cell {
			.complaint-info {
				.complaint-name {
					font-size: 12px;
				}

				.complaint-percentage {
					.percentage-value {
						font-size: 12px;
						padding: 4px 8px;
					}

					.percentage-bar {
						width: 100%;
						height: 4px;
					}
				}
			}

			.no-complaints {
				.no-data-text {
					font-size: 12px;
				}
			}
		}
	}

	@media (max-width: 480px) {
		.table-row {
			padding: 8px;

			.table-cell {
				font-size: 12px;

				&:before {
					font-size: 10px;
					min-width: 80px;
				}

				&.station-name {
					font-size: 14px;
				}
			}
		}

		.top-complaints-cell {
			.complaint-info {
				.complaint-name {
					font-size: 11px;
				}

				.complaint-percentage {
					.percentage-value {
						font-size: 11px;
						padding: 3px 6px;
					}
				}
			}
		}
	}

	// Update grid template for the new column
	@media (min-width: 769px) {
		.table-row {
			grid-template-columns: minmax(140px, 1.5fr) repeat(auto-fit, minmax(90px, 1fr));

			&.narrowed {
				grid-template-columns: 1.5fr 1fr 1.2fr 1fr 1.2fr 1fr;
			}
		}
	}
}
</style>
